import type { CycleEntry } from "./types";
import type { CycleTreeNode, GroupSummary } from "./selectors";

/** What the Passes tree is ordered by. */
export type SortKey = "startTime" | "parserTime" | "reactTime" | "wallTime" | "cycleCount";
export type SortDirection = "asc" | "desc";
export type PassSort = { key: SortKey; direction: SortDirection };

export const DEFAULT_SORT: PassSort = { key: "startTime", direction: "asc" };

/** Natural direction when a key is first picked: metrics read hottest-first, start time reads chronologically. */
export const defaultDirection = (key: SortKey): SortDirection => (key === "startTime" ? "asc" : "desc");

export const SORT_KEY_LABELS: Record<SortKey, string> = {
    startTime: "Start time",
    parserTime: "Parser time",
    reactTime: "React commit",
    wallTime: "Wall time",
    cycleCount: "Cycle count",
};

const groupValue = (g: GroupSummary, key: SortKey): number => {
    switch (key) {
        case "startTime": return g.startTime;
        case "parserTime": return g.parserTime;
        case "reactTime": return g.reactTime;
        case "wallTime": return g.wallTime;
        case "cycleCount": return g.cycleCount;
    }
};

// Ties break on startTime ascending regardless of key/direction, so equal passes keep a stable,
// chronological order instead of flipping with the direction toggle.
const compareWithTieBreak = (direction: SortDirection, a: number, b: number, aStart: number, bStart: number): number => {
    const primary = direction === "asc" ? a - b : b - a;
    return primary !== 0 ? primary : aStart - bStart;
};

/** The passes ordered by the sort key/direction (input untouched). */
export function sortGroups(groups: GroupSummary[], sort: PassSort): GroupSummary[] {
    return groups.toSorted((a, b) =>
        compareWithTieBreak(sort.direction, groupValue(a, sort.key), groupValue(b, sort.key), a.startTime, b.startTime));
}

// A cycle's own contribution per metric (its batches only, descendants excluded).
function ownValue(cycle: CycleEntry, key: SortKey): number {
    switch (key) {
        case "startTime":
            return cycle.startTime;
        case "parserTime":
            return (cycle.render?.totalTime ?? 0) + cycle.callbacks.reduce((s, b) => s + b.totalTime, 0);
        case "reactTime":
            return cycle.react?.totalTime ?? 0;
        case "cycleCount":
            return 1;
        case "wallTime": {
            // Same span as summarizeCycle: earliest batch start to latest batch end.
            let min = Infinity, max = -Infinity;
            for (const b of [cycle.render, ...cycle.callbacks, cycle.react]) {
                if (!b) continue;
                min = Math.min(min, b.startTime);
                max = Math.max(max, b.startTime + b.totalTime);
            }
            return Number.isFinite(min) ? max - min : 0;
        }
    }
}

/**
 * Re-orders every `children` array of the tree in place by SUBTREE totals — a child ranks by its own
 * batches plus all nested descendant cycles, so a cheap wrapper around a hot nested template still
 * bubbles up.  `startTime` is the exception: chronological order is a property of the cycle itself,
 * not its branch.  Mutation is safe because `cycleTree()` builds fresh nodes on every call.
 */
export function sortCycleTree(root: CycleTreeNode, sort: PassSort): CycleTreeNode {
    const subtree = new Map<CycleTreeNode, number>();
    const measure = (node: CycleTreeNode): number => {
        const own = ownValue(node.cycle, sort.key);
        const value = sort.key === "startTime"
            ? own
            : node.children.reduce((s, c) => s + measure(c), own);
        subtree.set(node, value);
        // startTime skips the children fold above, so descend explicitly to fill the cache.
        if (sort.key === "startTime") node.children.forEach(measure);
        return value;
    };
    measure(root);

    const sortRec = (node: CycleTreeNode) => {
        node.children.sort((a, b) =>
            compareWithTieBreak(sort.direction, subtree.get(a)!, subtree.get(b)!, a.cycle.startTime, b.cycle.startTime));
        node.children.forEach(sortRec);
    };
    sortRec(root);
    return root;
}
